import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject, fromEvent } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';

import {
  ProductProgramBenefitCard,
  ProductProgramBenefitDescriptionLink,
  ProductProgramBenefitStat,
} from './product-program-benefits.interface';

@Component({
  selector: 'app-product-program-benefits',
  templateUrl: './product-program-benefits.component.html',
  styleUrls: ['./product-program-benefits.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductProgramBenefitsComponent implements AfterViewInit, OnDestroy {
  private readonly DEFAULT_HEADING_ID = 'product-program-benefits-heading';
  private readonly _destroy$ = new Subject<void>();
  private readonly DESKTOP_MIN_WIDTH_PX = 768;
  private readonly isBrowser: boolean;
  private _destroyed = false;

  @Input() headline = '';
  @Input() description = '';
  @Input() descriptionLink?: ProductProgramBenefitDescriptionLink;
  @Input() headingId = '';
  @Input() stats: ProductProgramBenefitStat[] = [];
  @Input() cards: ProductProgramBenefitCard[] = [];

  @ViewChildren('cardItem') private readonly _cardItems!: QueryList<ElementRef<HTMLLIElement>>;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private readonly _ngZone: NgZone,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  get resolvedHeadingId(): string {
    const id = this.headingId.trim();
    return id || this.DEFAULT_HEADING_ID;
  }

  get hasStats(): boolean {
    return this.stats.length > 0;
  }

  get hasCards(): boolean {
    return this.cards.length > 0;
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this._ngZone.runOutsideAngular(() => {
      this._scheduleHeightEqualization();
      this._cardItems.changes.pipe(takeUntil(this._destroy$)).subscribe(() => {
        this._scheduleHeightEqualization();
      });
      fromEvent(window, 'resize')
        .pipe(auditTime(100), takeUntil(this._destroy$))
        .subscribe(() => this._equalizeCardHeights());
    });
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _scheduleHeightEqualization(): void {
    if (!this.isBrowser) return;
    // Double rAF ensures all c11n-card child rendering is complete before measuring.
    // Falls back to nested setTimeout in environments where rAF is unavailable (e.g. JSDOM).
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this._equalizeCardHeights());
      });
    } else {
      setTimeout(() => setTimeout(() => this._equalizeCardHeights()));
    }
  }

  private _equalizeCardHeights(): void {
    if (this._destroyed) return;
    const items = this._cardItems?.toArray();
    if (!items?.length) return;

    items.forEach(item => (item.nativeElement.style.minHeight = ''));

    if (!this.isBrowser || window.innerWidth < this.DESKTOP_MIN_WIDTH_PX) return;

    const maxHeight = Math.max(...items.map(item => item.nativeElement.offsetHeight));
    if (maxHeight > 0) {
      items.forEach(item => (item.nativeElement.style.minHeight = `${maxHeight}px`));
    }
  }

  trackByStatIndex(index: number): number {
    return index;
  }

  trackByCardIndex(index: number): number {
    return index;
  }
}
